// ============================================
// Tag-Specific Detail Page Mock Data - vLLM
// ============================================

// vLLM Instance Details
export interface VLLMStreamingStats {
  totalChunks: number;
  totalTokens: number;
  tokensPerSecond: number;
  totalDuration: string;
  timeToFirstToken: number;
  status: 'streaming' | 'completed' | 'error';
}

export interface VLLMRequestTrace {
  id: string;
  timestamp: string;
  method: 'POST';
  endpoint: string;
  model: string;
  status: 'success' | 'error';
  latencyMs: number;
  inputTokens: number;
  outputTokens: number;
  messages: { role: string; content: string }[];
  response: string;
  streamingStats?: VLLMStreamingStats;
}

export interface VLLMProductionMetrics {
  requestsPerSecond: number;
  avgLatencyMs: number;
  p50LatencyMs: number;
  p95LatencyMs: number;
  p99LatencyMs: number;
  tokensPerSecond: number;
  errorRate: number;
  activeConnections: number;
  queuedRequests: number;
  gpuUtilization: number;
  gpuMemoryUsed: number;
  gpuMemoryTotal: number;
  uptime: string;
  totalRequests24h: number;
  totalTokens24h: number;
}

export interface VLLMBotPrevention {
  enabled: boolean;
  rateLimit: {
    enabled: boolean;
    requestsPerMinute: number;
    requestsPerHour: number;
    burstLimit: number;
    blockedIPs: string[];
    whitelistedIPs: string[];
  };
  proofOfWork: {
    enabled: boolean;
    difficulty: 'low' | 'medium' | 'high' | 'adaptive';
    algorithm: 'sha256';
    validitySeconds: number;
    exemptAuthenticatedUsers: boolean;
    challengeEndpoint: string;
    protectedEndpoint: string;
  };
  stats: {
    blockedRequests24h: number;
    rateLimitedRequests24h: number;
    powChallengesSolved24h: number;
    avgPowSolveTimeMs: number;
  };
}

export interface VLLMInstanceDetails {
  modelName: string;
  modelSize: string;
  contextLength: number;
  maxTokens: number;
  endpoint: string;
  apiKey: string;
  status: 'healthy' | 'degraded' | 'offline';
  productionMetrics: VLLMProductionMetrics;
  config: {
    tensorParallel: number;
    dtype: string;
    quantization: string | null;
    maxModelLen: number;
    gpuMemoryUtilization: number;
  };
  botPrevention: VLLMBotPrevention;
  recentRequests: VLLMRequestTrace[];
  liveStream: {
    isActive: boolean;
    currentRequest?: {
      model: string;
      messages: { role: string; content: string }[];
      stream: boolean;
      maxTokens: number;
    };
    streamingStats?: VLLMStreamingStats;
    chunks: { index: number; text: string }[];
  };
}

export const mockVLLMInstanceDetails = (taskId: string): VLLMInstanceDetails => ({
  modelName: 'typhoon-v2-70b-instruct',
  modelSize: '70B',
  contextLength: 128000,
  maxTokens: 4096,
  endpoint: `https://api.float16.cloud/v1/gpu/${taskId}/chat/completions`,
  apiKey: `sk-vllm-${taskId.slice(0, 8)}-${Math.random().toString(36).slice(2, 10)}`,
  status: 'healthy',
  productionMetrics: {
    requestsPerSecond: 12.5,
    avgLatencyMs: 245,
    p50LatencyMs: 180,
    p95LatencyMs: 450,
    p99LatencyMs: 890,
    tokensPerSecond: 850,
    errorRate: 0.2,
    activeConnections: 8,
    queuedRequests: 2,
    gpuUtilization: 78,
    gpuMemoryUsed: 68,
    gpuMemoryTotal: 80,
    uptime: '5d 12h 34m',
    totalRequests24h: 45230,
    totalTokens24h: 12500000,
  },
  config: {
    tensorParallel: 1,
    dtype: 'float16',
    quantization: null,
    maxModelLen: 32768,
    gpuMemoryUtilization: 0.9,
  },
  botPrevention: {
    enabled: true,
    rateLimit: {
      enabled: true,
      requestsPerMinute: 60,
      requestsPerHour: 1000,
      burstLimit: 10,
      blockedIPs: ['192.168.1.100', '10.0.0.50'],
      whitelistedIPs: ['10.0.0.1', '192.168.1.1'],
    },
    proofOfWork: {
      enabled: true,
      difficulty: 'adaptive',
      algorithm: 'sha256',
      validitySeconds: 300,
      exemptAuthenticatedUsers: true,
      challengeEndpoint: `/v1/gpu/${taskId}/pow/challenge`,
      protectedEndpoint: `/v1/gpu/${taskId}/pow/chat/completions`,
    },
    stats: {
      blockedRequests24h: 1247,
      rateLimitedRequests24h: 3891,
      powChallengesSolved24h: 28450,
      avgPowSolveTimeMs: 145,
    },
  },
  recentRequests: [
    {
      id: 'req-001',
      timestamp: new Date(Date.now() - 30000).toISOString(),
      method: 'POST',
      endpoint: '/v1/chat/completions',
      model: 'typhoon-v2-70b-instruct',
      status: 'success',
      latencyMs: 3700,
      inputTokens: 45,
      outputTokens: 156,
      messages: [
        { role: 'system', content: 'You are a helpful assistant.' },
        { role: 'user', content: 'What is Float16?' },
      ],
      response: 'Float16 is a GPU management platform that helps developers deploy and manage LLMs on their own infrastructure with ease...',
      streamingStats: {
        totalChunks: 47,
        totalTokens: 156,
        tokensPerSecond: 42.3,
        totalDuration: '3.7s',
        timeToFirstToken: 120,
        status: 'completed',
      },
    },
    {
      id: 'req-002',
      timestamp: new Date(Date.now() - 120000).toISOString(),
      method: 'POST',
      endpoint: '/v1/chat/completions',
      model: 'typhoon-v2-70b-instruct',
      status: 'success',
      latencyMs: 2100,
      inputTokens: 32,
      outputTokens: 89,
      messages: [
        { role: 'user', content: 'Explain NVIDIA MIG in one paragraph.' },
      ],
      response: 'NVIDIA Multi-Instance GPU (MIG) technology allows a single GPU to be partitioned into multiple smaller instances...',
      streamingStats: {
        totalChunks: 28,
        totalTokens: 89,
        tokensPerSecond: 38.5,
        totalDuration: '2.1s',
        timeToFirstToken: 95,
        status: 'completed',
      },
    },
    {
      id: 'req-003',
      timestamp: new Date(Date.now() - 300000).toISOString(),
      method: 'POST',
      endpoint: '/v1/chat/completions',
      model: 'typhoon-v2-70b-instruct',
      status: 'success',
      latencyMs: 5200,
      inputTokens: 128,
      outputTokens: 256,
      messages: [
        { role: 'system', content: 'You are a code assistant.' },
        { role: 'user', content: 'Write a Python function to calculate fibonacci numbers.' },
      ],
      response: 'def fibonacci(n):\n    if n <= 1:\n        return n\n    return fibonacci(n-1) + fibonacci(n-2)...',
      streamingStats: {
        totalChunks: 72,
        totalTokens: 256,
        tokensPerSecond: 45.2,
        totalDuration: '5.2s',
        timeToFirstToken: 110,
        status: 'completed',
      },
    },
  ],
  liveStream: {
    isActive: true,
    currentRequest: {
      model: 'typhoon-v2-70b-instruct',
      messages: [
        { role: 'system', content: 'You are a helpful assistant.' },
        { role: 'user', content: 'What is Float16?' },
      ],
      stream: true,
      maxTokens: 256,
    },
    streamingStats: {
      totalChunks: 47,
      totalTokens: 156,
      tokensPerSecond: 42.3,
      totalDuration: '3.7s',
      timeToFirstToken: 120,
      status: 'completed',
    },
    chunks: [
      { index: 1, text: 'Float16' },
      { index: 2, text: ' is' },
      { index: 3, text: ' a' },
      { index: 4, text: ' GPU' },
      { index: 5, text: ' management' },
      { index: 6, text: ' platform' },
      { index: 7, text: ' that' },
      { index: 8, text: ' helps' },
      { index: 9, text: ' developers' },
      { index: 10, text: ' deploy' },
      { index: 11, text: ' and' },
      { index: 12, text: ' manage' },
      { index: 13, text: ' LLMs' },
      { index: 14, text: ' on' },
      { index: 15, text: ' their' },
      { index: 16, text: ' own' },
      { index: 17, text: ' infrastructure' },
      { index: 18, text: ' with' },
      { index: 19, text: ' ease...' },
    ],
  },
});
